import React, { useState } from 'react';

const MMT_GRADES  = ['0','1','2','3','4','5'];
const TONE_GRADES = ['N','MH','H','S'];

const MUSCLE_DATA = {
  mmt: [
    { joint:'Shoulder', side:'upper', muscles:['Flexors','Extensors','Abductors','Adductors','Ext. Rotators','Int. Rotators'] },
    { joint:'Elbow',    side:'upper', muscles:['Flexors','Extensors'] },
    { joint:'Forearm',  side:'upper', muscles:['Pronators','Supinators'] },
    { joint:'Wrist',    side:'upper', muscles:['Flexors','Extensors','Radial Deviators','Ulnar Deviators'] },
    { joint:'Hand',     side:'upper', muscles:['Intrinsics','Extrinsics'] },
    { joint:'Hip',      side:'lower', muscles:['Flexors','Extensors','Abductors','Adductors','Ext. Rotators','Int. Rotators'] },
    { joint:'Knee',     side:'lower', muscles:['Flexors','Extensors'] },
    { joint:'Ankle',    side:'lower', muscles:['Dorsiflexors','Plantarflexors'] },
    { joint:'Foot',     side:'lower', muscles:['Invertors','Evertors','Intrinsics','Extrinsics'] },
  ],
  tone: [
    { joint:'Shoulder', side:'upper', muscles:['Flexors','Extensors','Abductors','Adductors'] },
    { joint:'Elbow',    side:'upper', muscles:['Flexors','Extensors'] },
    { joint:'Wrist',    side:'upper', muscles:['Flexors','Extensors'] },
    { joint:'Hand',     side:'upper', muscles:['Intrinsics','Finger Flexors'] },
    { joint:'Hip',      side:'lower', muscles:['Flexors','Extensors','Abductors','Adductors'] },
    { joint:'Knee',     side:'lower', muscles:['Flexors','Extensors'] },
    { joint:'Ankle',    side:'lower', muscles:['Dorsiflexors','Plantarflexors'] },
  ],
};

/* ── grade colour ── */
const gradeColor = (tab, g) => {
  if (!g) return null;
  if (tab === 'mmt') {
    if (g === '5') return { bg:'#ecfdf5', color:'#059669', border:'#6ee7b7' };
    if (g === '4') return { bg:'#eff6ff', color:'#2563eb', border:'#93c5fd' };
    if (g === '3') return { bg:'#fffbeb', color:'#d97706', border:'#fcd34d' };
    if (g === '2') return { bg:'#fff7ed', color:'#ea580c', border:'#fdba74' };
    return             { bg:'#fef2f2', color:'#dc2626', border:'#fca5a5' }; // 0, 1
  }
  if (g === 'N')  return { bg:'#ecfdf5', color:'#059669', border:'#6ee7b7' };
  if (g === 'MH') return { bg:'#fffbeb', color:'#d97706', border:'#fcd34d' };
  if (g === 'H')  return { bg:'#fff7ed', color:'#ea580c', border:'#fdba74' };
  return                 { bg:'#fef2f2', color:'#dc2626', border:'#fca5a5' }; // S
};

/* ── compact segmented grade bar (RT or LT) ── */
const GradeBar = ({ tab, value, onChange }) => {
  const grades = tab === 'mmt' ? MMT_GRADES : TONE_GRADES;
  const label  = g => tab === 'tone' ? g : g;
  return (
    <div style={{
      display:'flex', borderRadius:8,
      border:`1.5px solid ${value ? (gradeColor(tab,value)?.border||'#e2e8f0') : '#e2e8f0'}`,
      overflow:'hidden', background: value ? (gradeColor(tab,value)?.bg||'#f8fafc') : '#f8fafc',
      transition:'border-color 0.15s',
    }}>
      {grades.map((g,i) => {
        const sel = value === g;
        const c   = sel ? gradeColor(tab,g) : null;
        return (
          <button key={g} type="button"
            onClick={() => onChange(sel ? null : g)}
            style={{
              padding:'5px 0', width:tab==='mmt'?28:36,
              fontSize:11, fontWeight:700, cursor:'pointer', border:'none',
              borderLeft: i > 0 ? '1px solid #e2e8f0' : 'none',
              background: sel ? c.bg : 'transparent',
              color:      sel ? c.color : '#94a3b8',
              transition:'all 0.1s',
            }}
          >{label(g)}</button>
        );
      })}
    </div>
  );
};

/* ── muscle detail row (inside expanded joint) ── */
const MuscleRow = ({ tab, joint, muscle, state, onSelect }) => {
  const rtKey = `${tab}|${joint}|${muscle}|rt`;
  const ltKey = `${tab}|${joint}|${muscle}|lt`;
  const rtVal = state[rtKey] || null;
  const ltVal = state[ltKey] || null;
  const hasEntry = rtVal || ltVal;

  return (
    <div style={{
      display:'flex', alignItems:'center', gap:10, padding:'7px 14px 7px 28px',
      borderTop:'0.5px solid #f1f5f9', flexWrap:'wrap',
      background: hasEntry ? '#fafbff' : 'transparent',
    }}>
      <div style={{ fontSize:11, color:'#475569', fontStyle:'italic', flex:'0 0 130px', minWidth:110 }}>
        {muscle}
      </div>
      <div className="mmt-rt-lt">
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:9, fontWeight:700, color:'#94a3b8' }}>RT</span>
          <GradeBar tab={tab} value={rtVal} onChange={v => onSelect(rtKey, v)} />
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:9, fontWeight:700, color:'#94a3b8' }}>LT</span>
          <GradeBar tab={tab} value={ltVal} onChange={v => onSelect(ltKey, v)} />
        </div>
      </div>
    </div>
  );
};

/* ── joint row — Intact/ROM style ── */
const JointRow = ({ tab, jointData, state, onSelect, borderColor }) => {
  const { joint, muscles } = jointData;
  const statusKey = `${tab}|${joint}|__status`;
  const status    = state[statusKey] || null;   // null | 'normal' | 'grade'
  const isNormal  = status === 'normal';
  const isGrade   = status === 'grade';

  const normalLabel = tab === 'mmt' ? 'Normal 5/5' : 'Normal tone';
  const gradeLabel  = tab === 'mmt' ? 'Grade ▾'    : 'Assess ▾';

  const setStatus = (s) => {
    const next = status === s ? null : s;
    const partial = { [`${tab}|${joint}|__status`]: next };
    // if switching to normal, fill all muscles = top grade
    if (next === 'normal') {
      const topGrade = tab === 'mmt' ? '5' : 'N';
      muscles.forEach(m => {
        partial[`${tab}|${joint}|${m}|rt`] = topGrade;
        partial[`${tab}|${joint}|${m}|lt`] = topGrade;
      });
    }
    onSelect(partial);
  };

  // summarise entries when grade mode
  const gradeEntries = muscles.flatMap(m => {
    const rt = state[`${tab}|${joint}|${m}|rt`];
    const lt = state[`${tab}|${joint}|${m}|lt`];
    return [rt, lt].filter(Boolean);
  });
  const hasWeakness = gradeEntries.some(v =>
    tab === 'mmt' ? ['0','1','2','3'].includes(v) : ['MH','H','S'].includes(v)
  );

  return (
    <div style={{ borderBottom:'0.5px solid #f1f5f9' }}>
      {/* header */}
      <div style={{
        display:'flex', alignItems:'center', gap:10, padding:'9px 14px', flexWrap:'wrap',
        background: isNormal ? '#f0fdf4' : isGrade ? '#f5f8ff' : '#fff',
        transition:'background 0.15s',
      }}>
        <div style={{ flex:'0 0 110px', minWidth:90 }}>
          <div style={{ fontSize:12, fontWeight:700, color:'#374151' }}>{joint}</div>
          {isNormal && <div style={{ fontSize:9, color:'#059669', fontWeight:700 }}>✓ {normalLabel}</div>}
          {isGrade && hasWeakness && <div style={{ fontSize:9, color:'#dc2626', fontWeight:700 }}>⚠ Weakness noted</div>}
          {isGrade && !hasWeakness && gradeEntries.length > 0 && <div style={{ fontSize:9, color:'#2563eb', fontWeight:700 }}>Grades recorded</div>}
        </div>

        <button type="button" onClick={() => setStatus('normal')} style={{
          padding:'7px 16px', borderRadius:99, fontWeight:700, fontSize:12, cursor:'pointer',
          border:`1.5px solid ${isNormal ? '#10b981' : '#e2e8f0'}`,
          background: isNormal ? '#ecfdf5' : '#f8fafc',
          color:       isNormal ? '#059669' : '#94a3b8',
          transition:'all 0.15s', minHeight:34,
        }}>
          {isNormal ? '✓ ' : ''}{normalLabel}
        </button>

        <button type="button" onClick={() => setStatus('grade')} style={{
          padding:'7px 16px', borderRadius:99, fontWeight:700, fontSize:12, cursor:'pointer',
          border:`1.5px solid ${isGrade ? '#3b82f6' : '#e2e8f0'}`,
          background: isGrade ? '#eff6ff' : '#f8fafc',
          color:       isGrade ? '#2563eb' : '#94a3b8',
          transition:'all 0.15s', minHeight:34,
        }}>
          {gradeLabel}
        </button>
      </div>

      {/* expanded muscles */}
      {isGrade && (
        <div style={{ background:'#f8fafc', borderTop:`0.5px solid ${borderColor}` }}>
          {/* sub-header */}
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'4px 14px 3px 28px' }}>
            <div style={{ flex:'0 0 130px', fontSize:9, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em' }}>Muscle</div>
            {tab === 'mmt'
              ? <span style={{ fontSize:9, fontWeight:700, color:'#94a3b8' }}>Grade  0 → 5</span>
              : <span style={{ fontSize:9, fontWeight:700, color:'#94a3b8' }}>Tone  N / MH / H / S</span>}
          </div>
          {muscles.map(m => (
            <MuscleRow key={m} tab={tab} joint={joint} muscle={m} state={state}
              onSelect={(k,v) => onSelect({ [k]: v })} />
          ))}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   MAIN EXPORT
══════════════════════════════════════════════════════ */
const ExpandableMuscleTone = ({ label, tab, color, dashColor, bgColor, state, onSelect, onClear }) => {
  const [open, setOpen] = useState(false);

  const data  = MUSCLE_DATA[tab];
  const upper = data.filter(d => d.side === 'upper');
  const lower = data.filter(d => d.side === 'lower');

  const normalCount  = data.filter(d => state[`${tab}|${d.joint}|__status`] === 'normal').length;
  const gradedCount  = data.filter(d => state[`${tab}|${d.joint}|__status`] === 'grade').length;
  const assessedCount = normalCount + gradedCount;

  const hasWeakness = data.some(d => {
    if (state[`${tab}|${d.joint}|__status`] !== 'grade') return false;
    return d.muscles.some(m =>
      [state[`${tab}|${d.joint}|${m}|rt`], state[`${tab}|${d.joint}|${m}|lt`]]
        .filter(Boolean)
        .some(v => tab === 'mmt' ? ['0','1','2','3'].includes(v) : ['MH','H','S'].includes(v))
    );
  });

  const handleSelect = (partial) => {
    onSelect({ ...state, ...partial });
  };

  /* closed button */
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} style={{
        width:'100%', padding:'15px', borderRadius:14,
        border:'1px solid #e2e8f0', backgroundColor:'transparent', color,
        fontSize:'0.95rem', fontWeight:700, cursor:'pointer',
        display:'flex', justifyContent:'center', alignItems:'center', gap:10,
        transition:'all 0.2s', marginBottom:8,
      }}>
        + {label}
        {normalCount > 0 && (
          <span style={{ fontSize:11, padding:'2px 10px', borderRadius:20, background:'#059669', color:'#fff', fontWeight:800 }}>
            {normalCount} normal
          </span>
        )}
        {gradedCount > 0 && (
          <span style={{ fontSize:11, padding:'2px 10px', borderRadius:20,
            background: hasWeakness ? '#dc2626' : '#2563eb', color:'#fff', fontWeight:800 }}>
            {gradedCount} graded{hasWeakness ? ' · weakness' : ''}
          </span>
        )}
      </button>
    );
  }

  /* open panel */
  return (
    <div style={{ padding:20, marginBottom:8 }}>
      <style>{`.mmt-grid{display:grid;grid-template-columns:1fr 1fr;gap:0 16px}@media(max-width:480px){.mmt-grid{grid-template-columns:1fr!important}}.mmt-rt-lt{display:flex;align-items:center;gap:6px}@media(max-width:380px){.mmt-rt-lt{flex-direction:column;align-items:flex-start;gap:3px}}`}</style>
      {/* header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
        <span style={{ fontWeight:800, fontSize:'1rem', color }}>{label}</span>
        <button type="button" onClick={() => setOpen(false)}
          style={{ background:'none', border:'none', color:'#cbd5e1', fontSize:'1.6rem', cursor:'pointer', lineHeight:1 }}
          onMouseEnter={e => e.target.style.color='#ef4444'}
          onMouseLeave={e => e.target.style.color='#cbd5e1'}>×</button>
      </div>

      {/* summary bar */}
      <div style={{
        display:'flex', alignItems:'center', gap:14, padding:'10px 16px',
        borderRadius:12, marginBottom:14, flexWrap:'wrap',
        background: hasWeakness ? '#fef2f2' : assessedCount > 0 ? '#f0fdf4' : '#f8fafc',
        border:`1px solid ${hasWeakness ? '#fca5a5' : assessedCount > 0 ? '#6ee7b7' : '#e2e8f0'}`,
      }}>
        <div style={{ fontSize:22, fontWeight:800, color: hasWeakness ? '#dc2626' : assessedCount > 0 ? '#059669' : '#94a3b8' }}>
          {assessedCount}
        </div>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color: hasWeakness ? '#991b1b' : assessedCount > 0 ? '#065f46' : '#64748b' }}>
            {assessedCount === 0
              ? 'No joints assessed yet'
              : `${assessedCount} of ${data.length} joints assessed — ${normalCount} normal, ${gradedCount} with grades${hasWeakness ? ' · weakness detected' : ''}`}
          </div>
          <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>
            Select <strong>{tab==='mmt' ? 'Normal 5/5' : 'Normal tone'}</strong> for full strength, or <strong>{tab==='mmt' ? 'Grade ▾' : 'Assess ▾'}</strong> to record individual muscles
          </div>
        </div>
      </div>

      {/* two-column: upper / lower */}
      <div className="mmt-grid">
        {[{ label:'Upper Limb', joints:upper }, { label:'Lower Limb', joints:lower }].map(({ label:lbl, joints }) => (
          <div key={lbl} style={{ border:`0.5px solid ${dashColor}`, borderRadius:12, overflow:'hidden' }}>
            <div style={{ background:bgColor, padding:'6px 14px', borderBottom:`0.5px solid ${dashColor}` }}>
              <span style={{ fontSize:11, fontWeight:800, color, textTransform:'uppercase', letterSpacing:'0.07em' }}>{lbl}</span>
            </div>
            <div style={{ background:'#fff' }}>
              {joints.map(jd => (
                <JointRow key={jd.joint} tab={tab} jointData={jd} state={state}
                  onSelect={handleSelect} borderColor={dashColor} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {assessedCount > 0 && (
        <button type="button" onClick={onClear}
          style={{ marginTop:10, fontSize:12, background:'none', border:'none', color:'#94a3b8', cursor:'pointer', textDecoration:'underline' }}>
          Clear all
        </button>
      )}
    </div>
  );
};

export { ExpandableMuscleTone };
export default ExpandableMuscleTone;
